/* tslint:disable */
import * as R from 'ramda';
import uuidv4 from 'uuid/v4';
import {
  ConnectMutationVariables,
  CreateMutationVariables,
  DeleteMutationVariables,
  Resource,
  Item,
} from './genericTypes';
import {
  ObjectOrObjectArrayOrNull,
  isObjectTruthy,
  nullIfEmpty,
} from '../../crud-core/utils/TypescriptUtils';
import { diffArray } from './diffArray';
import { diffObject } from './diffObject';
import { computeCreateMutationVariables } from './computeCreateMutationVariables';
import { computeConnectMutationVariables } from './computeConnectMutationVariables';
import { computeDeleteMutationVariables } from './computeDeleteMutationVariables';

export type UpdateMutationVariables = {
  where: Object;
  update: Object;
  create: Object;
};

export type LinkedMutationVariables = {
  connect?: ConnectMutationVariables | Array<ConnectMutationVariables>;
  create?: CreateMutationVariables | Array<CreateMutationVariables>;
  update?: UpdateMutationVariables | Array<UpdateMutationVariables>;
  delete?: DeleteMutationVariables | Array<DeleteMutationVariables>;
  disconnect?: DeleteMutationVariables | Array<DeleteMutationVariables>;
};

export function computeUpdateMutationVariables(
  item: Item,
  resourceConfig: Resource,
  previousItem: Item,
  isCreate?: boolean,
): UpdateMutationVariables | null {
  let data: any = item;
  let ommittedRelationForCreate: string[] = [];
  let where: { [s: string]: string | { [s: string]: string } } = {};

  if (
    !isCreate &&
    (!item[resourceConfig.keyProperty] || !previousItem[resourceConfig.keyProperty])
  ) {
    throw new Error(
      `Property ${resourceConfig.keyProperty} is missing in ${resourceConfig.singular}. It is required for an update mutation.`,
    );
  }

  if (
    !isCreate &&
    !R.propEq(resourceConfig.keyProperty, previousItem[resourceConfig.keyProperty])(item)
  ) {
    throw new Error(
      `Given ${resourceConfig.plural} don't have the same ${resourceConfig.keyProperty} property. They must be the same to perform an update mutation.`,
    );
  }

  if (resourceConfig.linkedResources && resourceConfig.linkedResources.length > 0) {
    for (let linkedResource of resourceConfig.linkedResources) {
      if (
        typeof item[linkedResource.field] === 'undefined' &&
        typeof previousItem[linkedResource.field] === 'undefined'
      ) {
        continue;
      }

      let createList: ObjectOrObjectArrayOrNull = [];
      let connectList: ObjectOrObjectArrayOrNull = [];
      let updateList: ObjectOrObjectArrayOrNull = [];
      let deleteList: ObjectOrObjectArrayOrNull = [];

      if (Array.isArray(item[linkedResource.field])) {
        const { updated, deleted, created } = diffArray(
          data[linkedResource.field],
          previousItem ? previousItem[linkedResource.field] : [],
          linkedResource.resource,
        );

        if (linkedResource.resource.customMutation) {
          const { deleteCustom, createCustom, updateCustom, connectCustom } =
            linkedResource.resource.customMutation(
              updated,
              deleted,
              created,
              linkedResource.resource,
            );
          createList = createCustom;
          updateList = updateCustom;
          deleteList = deleteCustom;
          connectList = connectCustom;
        } else {
          createList =
            created &&
            nullIfEmpty(
              created
                .filter((i) => !R.has(resourceConfig.keyProperty)(i))
                .map((field: Object) =>
                  computeCreateMutationVariables(field, linkedResource.resource),
                ),
            );

          connectList =
            created &&
            nullIfEmpty(
              created
                .filter(R.has(resourceConfig.keyProperty))
                .map((field: Object) =>
                  computeConnectMutationVariables(field, linkedResource.resource),
                ),
            );

          updateList =
            updated &&
            updated.map((field: Object, index: number) =>
              computeUpdateMutationVariables(
                field,
                linkedResource.resource,
                previousItem && previousItem[linkedResource.field][index],
              ),
            );

          deleteList =
            deleted &&
            deleted.map((field: Object) =>
              computeDeleteMutationVariables(field, linkedResource.resource),
            );
        }
      } else {
        const { updated, deleted, created } = diffObject(
          item[linkedResource.field],
          previousItem ? previousItem[linkedResource.field] : [],
          linkedResource.resource,
        );

        createList =
          created &&
          !R.has(resourceConfig.keyProperty, created) &&
          computeCreateMutationVariables(created, linkedResource.resource);

        connectList =
          created &&
          R.has(resourceConfig.keyProperty, created) &&
          computeConnectMutationVariables(created, linkedResource.resource);

        updateList =
          updated &&
          computeUpdateMutationVariables(
            updated,
            linkedResource.resource,
            previousItem && previousItem[linkedResource.field],
          );

        deleteList = deleted && computeDeleteMutationVariables(deleted, linkedResource.resource);
      }

      if (createList || connectList || updateList || deleteList) {
        data[linkedResource.field] = {};
        if (createList && nullIfEmpty(createList) !== null) {
          data[linkedResource.field] = {
            create: createList,
            ...data[linkedResource.field],
          };
        }
        if (connectList && nullIfEmpty(connectList) !== null) {
          data[linkedResource.field] = {
            connect: connectList,
            ...data[linkedResource.field],
          };
        }
        if (updateList && nullIfEmpty(updateList) !== null) {
          data[linkedResource.field] = {
            update: updateList,
            ...data[linkedResource.field],
          };
        }
        if (deleteList && nullIfEmpty(deleteList) !== null) {
          data[linkedResource.field][
            linkedResource.resource.isRelationEnity ? 'delete' : 'disconnect'
          ] = deleteList;

          if (
            (data[linkedResource.field]['disconnect'] &&
              data[linkedResource.field]['disconnect'] === true) ||
            data[linkedResource.field]['delete']
          ) {
            ommittedRelationForCreate.push(linkedResource.field);
          }
        }
      } else {
        data = R.omit([linkedResource.field], data);
      }
    }
  }

  if (isObjectTruthy(data)) {
    const keyProperty = item[resourceConfig.keyProperty] || uuidv4();
    where = {
      [resourceConfig.keyProperty]: item[resourceConfig.keyProperty] || keyProperty,
    };
    if (!data[resourceConfig.keyProperty]) {
      data[resourceConfig.keyProperty] = keyProperty;
    }
    const update = R.omit(
      [resourceConfig.keyProperty, '__typename', 'updatedAt', 'deletedAt', 'createdAt'],
      data,
    );
    const create = R.omit(
      ['__typename', 'updatedAt', 'deletedAt', 'createdAt', ...ommittedRelationForCreate],
      data,
    );

    return { update, create, where };
  }

  return null;
}
